// User conversation history API types and functions

export interface ConversationSummary {
    session_id: string;
    first_question: string;
    timestamp: string;
    last_timestamp: string;
    message_count: number;
}

export interface ConversationMessage {
    id: string;
    timestamp: string;
    question: string;
    answer: string;
    sources: string[];
    feedback?: "positive" | "negative" | null;
}

export interface SearchResult {
    id: string;
    session_id: string;
    timestamp: string;
    question: string;
    answer: string;
    feedback?: "positive" | "negative" | null;
}

export async function fetchConversations(days = 90): Promise<ConversationSummary[]> {
    const resp = await fetch(`/api/my/conversations?days=${days}`);
    if (!resp.ok) throw new Error("Failed to fetch conversations");
    return resp.json();
}

export async function fetchConversationDetail(sessionId: string): Promise<ConversationMessage[]> {
    const resp = await fetch(`/api/my/conversations/${encodeURIComponent(sessionId)}`);
    if (!resp.ok) throw new Error("Failed to fetch conversation");
    return resp.json();
}

export async function submitFeedback(conversationId: string, feedback: "positive" | "negative", comment?: string): Promise<void> {
    const resp = await fetch(`/api/my/conversations/${encodeURIComponent(conversationId)}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ feedback, comment })
    });
    if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || "Failed to submit feedback");
    }
}

export async function searchConversations(query: string, days = 365): Promise<SearchResult[]> {
    const resp = await fetch(`/api/my/conversations/search?q=${encodeURIComponent(query)}&days=${days}`);
    if (!resp.ok) throw new Error("Failed to search conversations");
    return resp.json();
}

export function getExportUrl(sessionId: string, format: "text" | "markdown" = "text"): string {
    return `/api/my/conversations/${encodeURIComponent(sessionId)}/export?format=${format}`;
}

export async function getSessionInfo(): Promise<{ email: string; isAdmin: boolean } | null> {
    try {
        const resp = await fetch("/auth/session");
        if (!resp.ok) return null;
        const data = await resp.json();
        if (!data.authenticated) return null;

        let isAdmin = false;
        try {
            const adminResp = await fetch("/admin/api/session");
            if (adminResp.ok) {
                const adminData = await adminResp.json();
                isAdmin = adminData.is_admin === true;
            }
        } catch {
            // Admin check failed, default to false
        }
        return { email: data.email, isAdmin };
    } catch {
        return null;
    }
}

export async function logout(): Promise<void> {
    await fetch("/auth/logout", { method: "POST" });
}
